/**
 * Trang the thao: danh sach su kien va bang ty le.
 *
 * Bo cuc hai cot tren may tinh — danh sach ben trai, phieu cuoc dinh ben phai.
 * Man hinh hep chuyen phieu cuoc thanh tam truot tu duoi.
 *
 * Nut ty le la don vi thong tin day nhat cua man hinh nay: ten lua chon nho, con
 * SO thi lon va dung chu deu chan, vi nguoi choi quet mat theo cot so chu khong doc chu.
 */

import { useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type Market, type Selection, type SportEvent } from '../lib/api';
import { formatOdds } from '../lib/money';
import { useBetslip } from '../lib/betslip';
import { useSession } from '../lib/session';
import { BetSlip } from '../components/BetSlip';
import { EmptyState, Skeleton } from '../components/ui';
import styles from './Sports.module.css';

/** Nhan hien thi cho tung loai thi truong. */
const MARKET_LABEL: Readonly<Record<string, string>> = {
  '1X2': 'Toàn trận',
  ASIAN_HANDICAP: 'Kèo chấp',
  OVER_UNDER: 'Tài xỉu',
  DOUBLE_CHANCE: 'Cửa đôi',
  BTTS: 'Hai đội ghi bàn',
  CORRECT_SCORE: 'Tỷ số chính xác',
  CORNERS: 'Phạt góc',
};

function marketLabel(m: Market): string {
  const base = MARKET_LABEL[m.market_type] ?? m.market_type;
  return m.parameter ? `${base} ${m.parameter}` : base;
}

export function SportsPage(): JSX.Element {
  const { isAuthenticated } = useSession();
  const [sportCode, setSportCode] = useState<string | undefined>(undefined);
  const [live, setLive] = useState<boolean | undefined>(undefined);

  const sports = useQuery({
    queryKey: ['sports'],
    queryFn: api.sports.list,
    enabled: isAuthenticated,
    staleTime: 300_000,
  });

  const events = useQuery({
    queryKey: ['sport-events', sportCode, live],
    queryFn: () => api.sports.events(sportCode, live),
    enabled: isAuthenticated,
    // Ty le va trang thai su kien doi lien tuc; lam moi deu dan la du cho ban demo.
    // Ban that nen thay bang ket noi day du hai chieu (/v1/sports/stream).
    refetchInterval: 15_000,
    staleTime: 5_000,
  });

  if (!isAuthenticated) {
    return (
      <EmptyState
        title="Đăng nhập để xem kèo"
        description="Kèo và giới hạn nhận cược được tính riêng cho từng tài khoản."
      />
    );
  }

  return (
    <div className={styles.layout}>
      <div className={styles.main}>
        <div className={styles.filters}>
          <div className={styles.sports}>
            <button
              className={[styles.chip, sportCode === undefined ? styles.chipActive : '']
                .filter(Boolean)
                .join(' ')}
              onClick={() => setSportCode(undefined)}
            >
              Tất cả
            </button>
            {sports.data?.sports.map((s) => (
              <button
                key={s.code}
                className={[styles.chip, sportCode === s.code ? styles.chipActive : '']
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => setSportCode(s.code)}
              >
                {s.name}
                <span className={styles.chipCount}>{s.events}</span>
              </button>
            ))}
          </div>

          <div className={styles.timeFilter}>
            <button
              className={[styles.tab, live === undefined ? styles.tabActive : '']
                .filter(Boolean)
                .join(' ')}
              onClick={() => setLive(undefined)}
            >
              Tất cả
            </button>
            <button
              className={[styles.tab, live === true ? styles.tabActive : '']
                .filter(Boolean)
                .join(' ')}
              onClick={() => setLive(true)}
            >
              <span className={styles.liveDot} aria-hidden="true" />
              Trực tiếp
            </button>
            <button
              className={[styles.tab, live === false ? styles.tabActive : '']
                .filter(Boolean)
                .join(' ')}
              onClick={() => setLive(false)}
            >
              Sắp diễn ra
            </button>
          </div>
        </div>

        {events.isLoading ? (
          <div className={styles.events}>
            {Array.from({ length: 6 }, (_, i) => (
              <Skeleton key={i} height={72} />
            ))}
          </div>
        ) : events.data && events.data.events.length > 0 ? (
          <div className={styles.events}>
            {events.data.events.map((event) => (
              <EventRow key={event.id} event={event} />
            ))}
          </div>
        ) : (
          <EmptyState
            title="Chưa có sự kiện nào"
            description="Lịch thi đấu và tỷ lệ được nạp từ nhà cung cấp dữ liệu thể thao."
          />
        )}
      </div>

      <BetSlip />
    </div>
  );
}

// ---------------------------------------------------------------------------

function EventRow({ event }: { event: SportEvent }): JSX.Element {
  const [expanded, setExpanded] = useState(false);

  const markets = useQuery({
    queryKey: ['event-markets', event.id],
    queryFn: () => api.sports.markets(event.id),
    // Chi tai khi mo rong: mot trang co the co hang tram su kien.
    enabled: expanded,
    refetchInterval: expanded ? 15_000 : false,
  });

  // Thi truong chinh hien ngay o dong su kien, khong bat mo rong moi thay.
  const headline = useQuery({
    queryKey: ['event-headline', event.id],
    queryFn: () => api.sports.markets(event.id),
    refetchInterval: 20_000,
    staleTime: 10_000,
  });

  const mainMarket = headline.data?.markets.find((m) => m.market_type === '1X2') ??
    headline.data?.markets[0];

  const isLive = event.status === 'LIVE';
  const kickoff = new Date(event.starts_at);

  return (
    <article className={styles.event}>
      <div className={styles.eventMain}>
        <button
          className={styles.eventInfo}
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
        >
          <span className={styles.eventTime}>
            {isLive ? (
              <span className={styles.liveBadge}>
                <span className={styles.liveDot} aria-hidden="true" />
                TRỰC TIẾP
              </span>
            ) : (
              <>
                <span>{kickoff.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' })}</span>
                <span className="numeric">
                  {kickoff.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })}
                </span>
              </>
            )}
          </span>

          <span className={styles.teams}>
            <span className={styles.team}>{event.home_name}</span>
            <span className={styles.team}>{event.away_name ?? '—'}</span>
          </span>
        </button>

        <div className={styles.oddsGroup}>
          {mainMarket ? (
            mainMarket.selections.map((sel) => (
              <OddsButton
                key={sel.id}
                selection={sel}
                market={mainMarket}
                event={event}
                isLive={isLive}
              />
            ))
          ) : (
            <>
              <Skeleton height={40} />
              <Skeleton height={40} />
              <Skeleton height={40} />
            </>
          )}
        </div>

        <button
          className={styles.moreBtn}
          onClick={() => setExpanded((v) => !v)}
          aria-label={expanded ? 'Thu gọn thị trường' : 'Xem thêm thị trường'}
        >
          {expanded ? '−' : '+'}
        </button>
      </div>

      {expanded && (
        <div className={styles.markets}>
          {markets.isLoading ? (
            <Skeleton height={100} />
          ) : (
            markets.data?.markets.map((market) => (
              <div key={market.id} className={styles.market}>
                <div className={styles.marketHead}>
                  <span className={styles.marketName}>{marketLabel(market)}</span>
                  {market.status !== 'OPEN' && (
                    <span className={styles.marketSuspended}>Tạm dừng</span>
                  )}
                </div>
                <div className={styles.marketOdds}>
                  {market.selections.map((sel) => (
                    <OddsButton
                      key={sel.id}
                      selection={sel}
                      market={market}
                      event={event}
                      isLive={isLive}
                      showName
                    />
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </article>
  );
}

// ---------------------------------------------------------------------------

/**
 * Nut ty le.
 *
 * Nhay mau khi ty le doi: xanh khi tang, do khi giam. Day khong phai trang tri —
 * nguoi choi theo doi bang thi truong can thay ngay cho nao vua doi.
 */
function OddsButton({
  selection,
  market,
  event,
  isLive,
  showName = false,
}: {
  selection: Selection;
  market: Market;
  event: SportEvent;
  isLive: boolean;
  showName?: boolean;
}): JSX.Element {
  const slip = useBetslip();
  const selected = slip.has(selection.id);
  const disabled = selection.status !== 'OPEN' || market.status !== 'OPEN';

  const previous = useRef(selection.odds_bp);
  const [flash, setFlash] = useState<'up' | 'down' | null>(null);

  useEffect(() => {
    if (previous.current === selection.odds_bp) return;
    setFlash(selection.odds_bp > previous.current ? 'up' : 'down');
    previous.current = selection.odds_bp;
    const timer = setTimeout(() => setFlash(null), 1200);
    return () => clearTimeout(timer);
  }, [selection.odds_bp]);

  const label = useMemo(() => marketLabel(market), [market]);

  return (
    <button
      className={[
        styles.odds,
        selected ? styles.oddsSelected : '',
        flash === 'up' ? styles.flashUp : '',
        flash === 'down' ? styles.flashDown : '',
      ]
        .filter(Boolean)
        .join(' ')}
      disabled={disabled}
      onClick={() =>
        slip.toggle({
          selectionId: selection.id,
          eventId: event.id,
          marketId: market.id,
          // Ghi lai ty le TAI THOI DIEM BAM; may chu se doi chieu (SPT-R20).
          capturedOddsBp: selection.odds_bp,
          selectionName: selection.name,
          marketLabel: label,
          eventLabel: `${event.home_name} - ${event.away_name ?? ''}`.trim(),
          isLive,
        })
      }
      aria-pressed={selected}
    >
      {showName && <span className={styles.oddsName}>{selection.name}</span>}
      <span className={`numeric ${styles.oddsValue}`}>{formatOdds(selection.odds_bp)}</span>
    </button>
  );
}
